import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { FeatureFlagActionEnum } from '../flags/feature-flag-action.enum';

import { IFeatureFlagAuditEntry } from './feature-flag-audit-entry.interface';

// Reverse-proxied backend prefix (see proxy.conf.json / nginx) → feature-flag-service.
const AUDIT_PATH = '/service/audit';

/**
 * The fields of the service's `GET /audit` response that the audit-log view needs.
 */
interface IServerAuditEntry {
  at: string;
  flagKey: string;
  action: FeatureFlagActionEnum;
  next: boolean;
  actor: string;
  reason?: string;
}

/**
 * Reads the instance-wide feature-flag audit log from the service's `GET /audit` route
 * (all flags combined, newest first). The per-flag history lives on `GET /flags/:key/audit`.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagAuditService {
  private readonly http = inject(HttpClient);

  getAuditEntries(): Observable<IFeatureFlagAuditEntry[]> {
    return this.http.get<IServerAuditEntry[]>(AUDIT_PATH).pipe(
      map(entries =>
        entries.map(
          (entry): IFeatureFlagAuditEntry => ({
            at: entry.at,
            flagKey: entry.flagKey,
            action: entry.action,
            next: entry.next,
            actor: entry.actor,
            // Keep `reason` absent (not `undefined`) when the backend omits it, per the interface contract.
            ...(entry.reason !== undefined && { reason: entry.reason }),
          }),
        ),
      ),
    );
  }
}
